/**
 * blockRegistry.ts — Catálogo único de blocos do Studio
 *
 * Registro central consumido pelo editor, pelos renderizadores (web, PDF,
 * e-mail) e pela validação de publicação. Módulos registram seus blocos de
 * domínio aqui; nenhum renderizador mantém lista própria.
 *
 * RELEASE: v1.1.0 - 2026-09-11 - Bloco de colunas ganha grade de 12 colunas com
 *           proporções prontas por tamanho de tela (motor de composições, Fase 2)
 * RELEASE: v1.0.0 - 2026-09-09 - Versão inicial com blocos de layout e conteúdo
 */

import type {
  StudioBlockDefinition,
  StudioBlockFamily,
  StudioOutput,
} from './types';
import { STUDIO_GRID_PRESET_OPTIONS } from './layout/grid';

/** Mapa interno chave -> definição. */
const registry = new Map<string, StudioBlockDefinition>();

/**
 * Registra (ou substitui) um bloco no catálogo.
 * Substituir é permitido para suportar recarga em desenvolvimento.
 */
export function registerBlock(definition: StudioBlockDefinition): void {
  registry.set(definition.key, definition);
}

/** Registra vários blocos de uma vez. */
export function registerBlocks(definitions: StudioBlockDefinition[]): void {
  definitions.forEach(registerBlock);
}

/** Retorna a definição de um bloco, ou `undefined` se não existir. */
export function getBlock(key: string): StudioBlockDefinition | undefined {
  return registry.get(key);
}

/** Lista todos os blocos registrados. */
export function listBlocks(filtros?: {
  family?: StudioBlockFamily;
  output?: StudioOutput;
}): StudioBlockDefinition[] {
  let itens = Array.from(registry.values());
  if (filtros?.family) itens = itens.filter((b) => b.family === filtros.family);
  if (filtros?.output) itens = itens.filter((b) => b.outputs.includes(filtros.output!));
  return itens.sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
}

/** Capacidades entregues por um conjunto de chaves de bloco. */
export function capabilitiesOf(blockKeys: string[]): Set<string> {
  const capacidades = new Set<string>();
  blockKeys.forEach((key) => {
    getBlock(key)?.provides?.forEach((cap) => capacidades.add(cap));
  });
  return capacidades;
}

// =============================================================================
// BLOCOS BASE — layout e conteúdo (disponíveis para todos os tipos de modelo)
// =============================================================================

const TODAS_SAIDAS: StudioOutput[] = ['web', 'pdf', 'email'];

registerBlocks([
  {
    key: 'layout.section',
    label: 'Seção',
    family: 'layout',
    icon: 'square',
    outputs: TODAS_SAIDAS,
    container: true,
    props: [
      { key: 'backgroundColor', label: 'Cor de fundo', type: 'color' },
      { key: 'padding', label: 'Espaçamento interno', type: 'spacing', defaultValue: '24px' },
      { key: 'maxWidth', label: 'Largura máxima', type: 'text', defaultValue: '720px' },
    ],
  },
  {
    key: 'layout.columns',
    label: 'Colunas',
    family: 'layout',
    icon: 'columns',
    outputs: TODAS_SAIDAS,
    container: true,
    props: [
      {
        key: 'count',
        label: 'Quantidade de colunas',
        type: 'select',
        defaultValue: '2',
        options: [
          { value: '2', label: '2 colunas' },
          { value: '3', label: '3 colunas' },
          { value: '4', label: '4 colunas' },
        ],
        help: 'Usada quando a proporção está em "Automático".',
      },
      // Grade interna de 12 colunas (Fase 2): o usuário escolhe uma PROPORÇÃO
      // pronta e pode ajustá-la por tamanho de tela, sem lidar com números.
      {
        key: 'preset',
        label: 'Proporção das colunas',
        type: 'responsive',
        defaultValue: 'auto',
        of: {
          label: 'Proporção',
          type: 'select',
          options: STUDIO_GRID_PRESET_OPTIONS,
        },
      },
      { key: 'gap', label: 'Espaço entre colunas', type: 'spacing', defaultValue: '16px' },
      {
        key: 'alignItems',
        label: 'Alinhamento vertical',
        type: 'select',
        defaultValue: 'stretch',
        advanced: true,
        options: [
          { value: 'stretch', label: 'Mesma altura' },
          { value: 'start', label: 'No topo' },
          { value: 'center', label: 'No meio' },
          { value: 'end', label: 'Embaixo' },
        ],
      },
    ],
  },
  {
    key: 'layout.spacer',
    label: 'Espaçador',
    family: 'layout',
    icon: 'move-vertical',
    outputs: TODAS_SAIDAS,
    props: [{ key: 'height', label: 'Altura', type: 'text', defaultValue: '24px' }],
  },
  {
    key: 'layout.divider',
    label: 'Divisor',
    family: 'layout',
    icon: 'minus',
    outputs: TODAS_SAIDAS,
    props: [
      { key: 'color', label: 'Cor', type: 'color', defaultValue: '#e5e7eb' },
      { key: 'thickness', label: 'Espessura', type: 'text', defaultValue: '1px' },
    ],
  },
  {
    key: 'common.text',
    label: 'Texto',
    family: 'content',
    icon: 'type',
    outputs: TODAS_SAIDAS,
    props: [
      { key: 'html', label: 'Conteúdo', type: 'richtext', defaultValue: '<p>Digite seu texto</p>' },
      { key: 'align', label: 'Alinhamento', type: 'select', defaultValue: 'left', options: [
        { value: 'left', label: 'Esquerda' },
        { value: 'center', label: 'Centro' },
        { value: 'right', label: 'Direita' },
        { value: 'justify', label: 'Justificado' },
      ] },
      { key: 'color', label: 'Cor do texto', type: 'color' },
    ],
  },
  {
    key: 'common.image',
    label: 'Imagem',
    family: 'content',
    icon: 'image',
    outputs: TODAS_SAIDAS,
    props: [
      { key: 'src', label: 'Arquivo', type: 'image' },
      { key: 'alt', label: 'Texto alternativo', type: 'text' },
      { key: 'width', label: 'Largura', type: 'text', defaultValue: '100%' },
      { key: 'align', label: 'Alinhamento', type: 'select', defaultValue: 'center', options: [
        { value: 'left', label: 'Esquerda' },
        { value: 'center', label: 'Centro' },
        { value: 'right', label: 'Direita' },
      ] },
    ],
  },
  {
    key: 'common.button',
    label: 'Botão',
    family: 'content',
    icon: 'mouse-pointer-click',
    outputs: ['web', 'email'],
    props: [
      { key: 'label', label: 'Texto do botão', type: 'text', defaultValue: 'Clique aqui' },
      { key: 'href', label: 'Endereço de destino', type: 'text' },
      { key: 'backgroundColor', label: 'Cor de fundo', type: 'color' },
      { key: 'textColor', label: 'Cor do texto', type: 'color' },
    ],
  },
  {
    key: 'common.table',
    label: 'Tabela',
    family: 'content',
    icon: 'table',
    outputs: TODAS_SAIDAS,
    props: [
      { key: 'html', label: 'Conteúdo da tabela', type: 'richtext' },
      { key: 'striped', label: 'Linhas alternadas', type: 'boolean', defaultValue: true },
    ],
  },
]);
